import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const WestminsterNewConstructionPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?city=Westminster&newConstruction=true&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching Westminster new construction:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>Westminster New Construction Homes | Brand New Builds | Truc Doan</title>
        <meta name="description" content="Explore brand new construction homes in Westminster, CA. Modern floor plans, energy-efficient features, builder warranties, and the latest designs. Contact Truc Doan for new builds." />
        <meta name="keywords" content="Westminster new construction homes, new builds Westminster CA, brand new homes Westminster, new development Westminster, modern homes Westminster, energy efficient homes Westminster, builder warranty Westminster, Truc Doan new construction" />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/westminster-new-construction`} />
        <meta property="og:title" content="Westminster New Construction Homes | Brand New Builds | Truc Doan" />
        <meta property="og:description" content="Discover brand new construction homes in Westminster. Modern floor plans, energy efficiency, and builder warranties included." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/westminster-new-construction`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Westminster, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": SEO_CONFIG.AGENT_PHONE,
            "email": SEO_CONFIG.AGENT_EMAIL,
            "url": `${SEO_CONFIG.DOMAIN}/westminster-new-construction`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Truc Doan specializes in new construction homes in Westminster, CA. Find brand new builds with modern floor plans, energy efficiency, and full builder warranties.",
            "areaServed": {
              "@type": "City",
              "name": "Westminster, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            }
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button onClick={() => navigate('/')} style={{ position: 'absolute', top: '20px', left: '20px', padding: '10px 20px', background: 'rgb(14, 165, 233)', color: 'rgb(243, 244, 246)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', zIndex: 10 }}>
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>Westminster New Construction Homes</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            Brand new builds in Westminster — modern design, energy efficiency, and full builder warranties.
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>Why Buy New Construction in Westminster?</h2>
          <p>New construction homes in Westminster offer everything today's buyers want: open-concept floor plans, smart home technology, energy-efficient systems, and modern finishes throughout. Unlike older homes that may need costly renovations, new builds are move-in ready with the latest building codes and design trends.</p>
          <p>Westminster is experiencing a wave of new development as builders recognize the city's growing demand. From infill projects to larger planned communities, new construction options range from single-family homes to luxury townhomes — all backed by comprehensive builder warranties.</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>New Construction Advantages</h2>
          <p>Here's why buyers are choosing brand new homes in Westminster over resale properties.</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>Modern Floor Plans</h3>
              <p>Open-concept layouts, large kitchen islands, walk-in closets, and flexible bonus rooms designed for how families actually live today.</p>
            </div>
            <div className="amenity">
              <h3>Energy Efficiency</h3>
              <p>Solar panels, dual-pane windows, high-efficiency HVAC systems, and LED lighting throughout mean lower utility bills from day one.</p>
            </div>
            <div className="amenity">
              <h3>Builder Warranties</h3>
              <p>Comprehensive warranties covering structural elements (10 years), mechanical systems (2 years), and workmanship (1 year) provide peace of mind.</p>
            </div>
            <div className="amenity">
              <h3>Smart Home Technology</h3>
              <p>Pre-wired for smart home systems including video doorbells, smart thermostats, USB outlets, and whole-home Wi-Fi infrastructure.</p>
            </div>
            <div className="amenity">
              <h3>New Developments in Westminster</h3>
              <p>Several new communities are underway in Westminster, offering various home sizes and price points to meet growing demand in this desirable city.</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>Westminster New Construction Market Snapshot</h2>
          <p>New construction homes in Westminster command premium pricing but offer significant value through modern features and zero maintenance costs.</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>Market Stat</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Median New Construction Price</td><td>~$900,000</td></tr>
              <tr><td>Most Popular Price Range</td><td>$750K – $1.2M</td></tr>
              <tr><td>Common Sizes</td><td>3–5 Bedrooms, 1,800–3,000 sq ft</td></tr>
              <tr><td>Builder Warranty</td><td>1–10 Years (varies by component)</td></tr>
              <tr><td>Energy Savings vs Older Homes</td><td>30–50% lower utility costs</td></tr>
            </tbody>
          </table>
        </section>

        <section className="properties-section">
          <h2>Westminster New Construction Currently Available</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>{t('property.loadingProperties')}</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div key={property.listing_key} className="property-card" onClick={() => navigate(`/listing/${property.listing_key}`)} style={{ backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center' }}>
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">{formatPropertyAddress(property)}, CA {property.postal_code}</div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>{t('landing.listedBy')}: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>New construction listings update frequently. Contact Truc Doan to get early access to upcoming Westminster developments.</p>
          )}
        </section>

        <section className="cta-section">
          <h2>Interested in Westminster New Construction?</h2>
          <p>Truc Doan has relationships with local builders and early access to new Westminster developments. Get on the list before these homes hit the public market.</p>
          <div className="cta-buttons">
            <a href={`mailto:${SEO_CONFIG.AGENT_EMAIL}?subject=Westminster New Construction Inquiry`} className="cta-button primary">{t('county.emailAgent')}</a>
            <a href={`tel:${SEO_CONFIG.AGENT_PHONE}`} className="cta-button secondary">{t('county.callAgent', { phone: SEO_CONFIG.AGENT_PHONE })}</a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>Search New Builds</button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information.</span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default WestminsterNewConstructionPage;
